import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  RotateCcw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Power,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";

// Built-in sample photo used for the live preview when the admin hasn't
// uploaded their own. Nikon Z series shot — kept consistent with the rest
// of the site's editorial photography (mirrorless only, no DSLR).
const DEFAULT_SAMPLE_URL =
  "https://images.pexels.com/photos/17345669/pexels-photo-17345669.jpeg";

const POSITION_LABELS_FR = {
  "center": "Centre",
  "top-left": "Haut gauche",
  "top": "Haut centre",
  "top-right": "Haut droite",
  "left": "Centre gauche",
  "right": "Centre droite",
  "bottom-left": "Bas gauche",
  "bottom": "Bas centre",
  "bottom-right": "Bas droite",
};
const POSITION_LABELS_EN = {
  "center": "Center",
  "top-left": "Top left",
  "top": "Top",
  "top-right": "Top right",
  "left": "Left",
  "right": "Right",
  "bottom-left": "Bottom left",
  "bottom": "Bottom",
  "bottom-right": "Bottom right",
};

const SignaturePanel = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'logo' | 'settings' | 'reset_logo' | 'preview'
  const [settings, setSettings] = useState({
    enabled: true,
    opacity: 0.28,
    size_ratio: 0.32,
    position: "center",
    custom_logo_url: null,
  });
  const [availablePositions, setAvailablePositions] = useState([]);
  const [usingCustom, setUsingCustom] = useState(false);

  // Live preview state
  const [sampleFile, setSampleFile] = useState(null); // File or null = use DEFAULT_SAMPLE_URL
  const [previewSrc, setPreviewSrc] = useState(null); // base64 PNG from /preview
  // Natural aspect ratio of the rendered preview — used to make the frame
  // adapt to portrait / landscape / square photos uploaded by the admin.
  // Stays null until the first onLoad measurement to avoid a 3/4 flash.
  const [previewAspect, setPreviewAspect] = useState(null);
  const sampleInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.get("/admin/watermark");
      setSettings(data.settings || {});
      setAvailablePositions(data.available_positions || []);
      setUsingCustom(Boolean(data.using_custom_logo));
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    queueMicrotask(() => load());
  }, [load]);

  // Generate a fresh preview whenever settings change
  const refreshPreview = useCallback(
    async (currentSettings, currentSampleFile) => {
      setBusy("preview");
      try {
        const ax = authedAxios();
        const form = new FormData();
        if (currentSampleFile) {
          form.append("file", currentSampleFile);
        } else {
          // Fetch the default sample image as a Blob and attach it
          const resp = await fetch(DEFAULT_SAMPLE_URL);
          const blob = await resp.blob();
          form.append("file", new File([blob], "sample.jpg", { type: blob.type }));
        }
        form.append("opacity", currentSettings.opacity);
        form.append("size_ratio", currentSettings.size_ratio);
        form.append("position", currentSettings.position);
        const { data } = await ax.post("/admin/watermark/preview", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPreviewSrc(data.preview_b64);
      } catch (err) {
        // Preview failures are non-blocking — just log
        console.warn("Preview failed", err);
      } finally {
        setBusy(null);
      }
    },
    [authedAxios]
  );

  // Auto-refresh preview after the panel loads (or after the custom logo
  // changes). NOT on every settings tweak — slider drags would spam the API.
  // The explicit refreshPreview() calls inside saveSettings/upload/reset
  // handlers cover those cases.
  useEffect(() => {
    if (!loading) {
      refreshPreview(settings, sampleFile);
    }
  }, [loading, usingCustom, refreshPreview]);

  const handleSettingChange = (field, value) => {
    setSettings((s) => ({ ...s, [field]: value }));
  };

  const saveSettings = async (partial) => {
    setBusy("settings");
    try {
      const ax = authedAxios();
      const { data } = await ax.put("/admin/watermark", partial);
      setSettings(data.settings || {});
      toast.success(lang === "fr" ? "Réglages enregistrés" : "Settings saved");
      // Refresh preview with the new settings
      refreshPreview(data.settings || settings, sampleFile);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(null);
    }
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    try {
      const ax = authedAxios();
      const form = new FormData();
      form.append("file", file);
      const { data } = await ax.post("/admin/watermark/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings(data.settings || {});
      setUsingCustom(true);
      toast.success(lang === "fr" ? "Signature personnalisée installée" : "Custom signature uploaded");
      refreshPreview(data.settings || settings, sampleFile);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(null);
    }
  };

  const handleResetLogo = async () => {
    if (!window.confirm(
      lang === "fr"
        ? "Revenir au logo Kiwami par défaut comme signature ?"
        : "Revert to the default Kiwami logo as signature?"
    )) return;
    setBusy("reset_logo");
    try {
      const ax = authedAxios();
      const { data } = await ax.delete("/admin/watermark/logo");
      setSettings(data.settings || {});
      setUsingCustom(false);
      toast.success(lang === "fr" ? "Logo Kiwami restauré" : "Default Kiwami logo restored");
      refreshPreview(data.settings || settings, sampleFile);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(null);
    }
  };

  const handleToggle = () => {
    saveSettings({ enabled: !settings.enabled });
  };

  const onSampleChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setSampleFile(f);
    refreshPreview(settings, f);
  };

  const opacityPct = Math.round((settings.opacity || 0) * 100);
  const sizePct = Math.round((settings.size_ratio || 0) * 100);

  const positionsLabel = useMemo(
    () => (lang === "fr" ? POSITION_LABELS_FR : POSITION_LABELS_EN),
    [lang]
  );

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#A19E9C] py-20">
        <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.3em]">
          {lang === "fr" ? "Chargement…" : "Loading…"}
        </span>
      </div>
    );
  }

  return (
    <section data-testid="admin-signature-panel" className="space-y-10">
      {/* ============== HEADER ============== */}
      <div>
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Identité visuelle" : "Visual identity"}
        </p>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-4">
          {lang === "fr" ? "Signature & watermark" : "Signature & watermark"}
        </h2>
        <p className="mt-3 text-sm text-[#A19E9C] max-w-2xl leading-relaxed">
          {lang === "fr"
            ? "Définis ton logo / ta signature qui sera apposé(e) sur chaque photo que tu publies dans le portfolio. Protection visuelle : impossible à retirer une fois la photo téléchargée."
            : "Set your logo / signature that will be stamped on every photo you publish to the portfolio. Visual protection: cannot be removed once the photo is downloaded."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* ============== LEFT — SETTINGS ============== */}
        <div className="space-y-6">
          {/* Toggle enabled */}
          <div className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#A19E9C] mb-1">
                {lang === "fr" ? "Statut" : "Status"}
              </div>
              <div className="font-serif-display text-xl text-[#F2EBE5]">
                {settings.enabled
                  ? (lang === "fr" ? "Watermark activé" : "Watermark active")
                  : (lang === "fr" ? "Watermark désactivé" : "Watermark disabled")}
              </div>
              <p className="text-[11px] text-[#A19E9C] mt-2 max-w-xs">
                {settings.enabled
                  ? (lang === "fr"
                      ? "Chaque nouvelle photo portfolio sera marquée."
                      : "Each new portfolio photo will be stamped.")
                  : (lang === "fr"
                      ? "Les nouvelles photos ne seront pas marquées."
                      : "New photos will not be stamped.")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggle}
              disabled={busy === "settings"}
              data-testid="signature-toggle"
              className={`flex items-center gap-2 px-4 py-2.5 border text-[10px] uppercase tracking-[0.25em] transition-colors whitespace-nowrap ${
                settings.enabled
                  ? "border-[#7CC57F]/60 text-[#7CC57F] bg-[#7CC57F]/5 hover:bg-[#7CC57F]/10"
                  : "border-[#A19E9C]/40 text-[#A19E9C] hover:text-[#F2EBE5]"
              }`}
            >
              <Power className="w-3.5 h-3.5" strokeWidth={1.5} />
              {settings.enabled
                ? (lang === "fr" ? "Activé" : "On")
                : (lang === "fr" ? "Désactivé" : "Off")}
            </button>
          </div>

          {/* Custom logo */}
          <div className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#A19E9C] mb-1">
                  {lang === "fr" ? "Ton logo / signature" : "Your logo / signature"}
                </div>
                <h3 className="font-serif-display text-xl text-[#F2EBE5]">
                  {usingCustom
                    ? (lang === "fr" ? "Signature personnalisée" : "Custom signature")
                    : (lang === "fr" ? "Logo Kiwami par défaut" : "Default Kiwami logo")}
                </h3>
              </div>
              <span
                className="text-[9px] uppercase tracking-[0.25em] px-2 py-1 border whitespace-nowrap flex items-center gap-1"
                style={{
                  borderColor: usingCustom ? "rgba(197,160,89,0.45)" : "rgba(161,158,156,0.25)",
                  color: usingCustom ? "#C5A059" : "#A19E9C",
                }}
              >
                {usingCustom ? (
                  <>
                    <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />
                    {lang === "fr" ? "Personnalisée" : "Custom"}
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3 h-3" strokeWidth={1.5} />
                    {lang === "fr" ? "Par défaut" : "Default"}
                  </>
                )}
              </span>
            </div>
            <p className="text-[11px] text-[#A19E9C] leading-relaxed">
              {lang === "fr"
                ? "Recommandé : PNG transparent, fond sombre ou logo en niveaux de gris. La signature sera convertie en blanc semi-transparent et appliquée sur tes photos."
                : "Recommended: transparent PNG, dark-on-white or grayscale logo. The signature will be converted to semi-transparent white and stamped on your photos."}
            </p>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/webp,image/jpeg"
              className="hidden"
              data-testid="signature-logo-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                handleUploadLogo(f);
              }}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={busy === "logo"}
                data-testid="signature-upload-btn"
                className="flex-1 kw-btn-ghost"
              >
                {busy === "logo" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                )}
                {lang === "fr" ? "Uploader ma signature" : "Upload my signature"}
              </button>
              {usingCustom && (
                <button
                  type="button"
                  onClick={handleResetLogo}
                  disabled={busy === "reset_logo"}
                  data-testid="signature-reset-btn"
                  className="px-3 py-2.5 border border-[#F2EBE5]/15 text-[#A19E9C] hover:text-[#E27474] hover:border-[#E27474]/40 transition-colors text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {lang === "fr" ? "Défaut" : "Default"}
                </button>
              )}
            </div>
          </div>

          {/* Position */}
          <div className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.25em] text-[#A19E9C]">
              {lang === "fr" ? "Position" : "Position"}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {availablePositions.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => saveSettings({ position: p })}
                  disabled={busy === "settings"}
                  data-testid={`signature-pos-${p}`}
                  className={`text-[10px] uppercase tracking-[0.15em] px-2 py-2 border transition-colors ${
                    settings.position === p
                      ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                      : "border-[#F2EBE5]/15 text-[#A19E9C] hover:text-[#F2EBE5] hover:border-[#F2EBE5]/30"
                  }`}
                >
                  {positionsLabel[p] || p}
                </button>
              ))}
            </div>
          </div>

          {/* Opacity */}
          <div className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.25em] text-[#A19E9C]">
                {lang === "fr" ? "Opacité" : "Opacity"}
              </div>
              <div className="text-[#C5A059] font-mono text-sm">{opacityPct}%</div>
            </div>
            <input
              type="range"
              min="5"
              max="90"
              step="1"
              value={opacityPct}
              onChange={(e) => handleSettingChange("opacity", parseInt(e.target.value, 10) / 100)}
              onMouseUp={() => saveSettings({ opacity: settings.opacity })}
              onTouchEnd={() => saveSettings({ opacity: settings.opacity })}
              data-testid="signature-opacity-slider"
              className="w-full accent-[#C5A059]"
            />
            <p className="text-[10px] text-[#A19E9C]">
              {lang === "fr"
                ? "Plus l'opacité est élevée, plus la signature est visible."
                : "Higher opacity = more visible signature."}
            </p>
          </div>

          {/* Size */}
          <div className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.25em] text-[#A19E9C]">
                {lang === "fr" ? "Taille" : "Size"}
              </div>
              <div className="text-[#C5A059] font-mono text-sm">{sizePct}%</div>
            </div>
            <input
              type="range"
              min="8"
              max="60"
              step="1"
              value={sizePct}
              onChange={(e) => handleSettingChange("size_ratio", parseInt(e.target.value, 10) / 100)}
              onMouseUp={() => saveSettings({ size_ratio: settings.size_ratio })}
              onTouchEnd={() => saveSettings({ size_ratio: settings.size_ratio })}
              data-testid="signature-size-slider"
              className="w-full accent-[#C5A059]"
            />
            <p className="text-[10px] text-[#A19E9C]">
              {lang === "fr"
                ? "Taille de la signature en % du côté le plus court de la photo."
                : "Signature size as % of the photo's shortest edge."}
            </p>
          </div>
        </div>

        {/* ============== RIGHT — LIVE PREVIEW ============== */}
        <div className="space-y-4">
          <div className="flex items-baseline justify-between">
            <p className="overline kw-accent-line">
              {lang === "fr" ? "Aperçu en direct" : "Live preview"}
            </p>
            <input
              ref={sampleInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="signature-sample-input"
              onChange={onSampleChange}
            />
            <button
              type="button"
              onClick={() => sampleInputRef.current?.click()}
              disabled={busy === "preview"}
              data-testid="signature-sample-btn"
              className="text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] hover:text-[#C5A059] transition-colors flex items-center gap-1.5"
            >
              <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
              {lang === "fr" ? "Tester sur ma photo" : "Test on my photo"}
            </button>
          </div>

          <div
            className={`relative bg-[#050505] border border-[#F2EBE5]/10 overflow-hidden mx-auto w-full max-w-[520px] transition-[aspect-ratio] duration-300 ${previewAspect ? "" : "aspect-[3/4]"}`}
            style={previewAspect ? { aspectRatio: previewAspect } : undefined}
          >
            {previewSrc ? (
              <img
                src={previewSrc}
                alt="Watermark preview"
                className="w-full h-full object-contain"
                data-testid="signature-preview-img"
                onLoad={(e) => {
                  const w = e.currentTarget.naturalWidth;
                  const h = e.currentTarget.naturalHeight;
                  if (w > 0 && h > 0) {
                    setPreviewAspect(w / h);
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#A19E9C] text-xs uppercase tracking-[0.25em]">
                {lang === "fr" ? "Génération de l'aperçu…" : "Generating preview…"}
              </div>
            )}
            {busy === "preview" && (
              <div className="absolute inset-0 bg-[#050505]/40 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" strokeWidth={1.5} />
              </div>
            )}
          </div>

          <div className="border-l-2 border-[#C5A059]/40 bg-[#0a0a0a] p-4 text-[11px] text-[#A19E9C] leading-relaxed">
            <p>
              <strong className="text-[#C5A059] font-normal">
                {lang === "fr" ? "Bon à savoir : " : "Good to know: "}
              </strong>
              {lang === "fr"
                ? "Les changements s'appliquent automatiquement aux NOUVELLES photos uploadées via Portfolio. Les anciennes photos déjà publiées gardent leur watermark précédent (utilise le script `watermark_existing_portfolio.py` pour les retraiter)."
                : "Changes apply automatically to NEW photos uploaded via Portfolio. Previously published photos keep their old watermark (use the `watermark_existing_portfolio.py` script to reprocess them)."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignaturePanel;
